import { useEffect } from 'react';
import { BrowserRouter, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider } from '@vuer-ai/react-helmet-async';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ProtectedRoute } from './common/auth/components/ProtectedRoute';
import { AuthProvider } from './common/auth/components/AuthContext';
import { NotificationProvider } from './common/components/notification/NotificationContext';
import { ApolloProvider } from '@apollo/client';
import { getClient } from './apollo';
import { AppThemeProvider } from './theme';
import { Layout } from './common/components/layout/Layout';
import { TransferSidebarProvider } from './context/TransferSidebarContext';
import { TransferManagerProvider } from './context/TransferManagerContext';
import { ProjectDirectoriesProvider } from './context/ProjectDirectoriesContext';
import { ElectronProvider } from './context/ElectronContext';
import { BridgeRequiredProvider } from './context/BridgeRequiredContext';
import { UpdateNotification } from './common/components/UpdateNotification';
import './services/webBridge'; // Initialize web bridge
import AppRoutes from './routes';
import { isSignalUiEnabled, SignalPreviewRoutes } from './routes/signal';
import type { DawSaveAction } from './types/electron';
import TransferErrorDialog from './common/components/TransferErrorDialog';

function DawSaveActionListener() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!window.electronAPI?.onDawSaveAction) return;
    return window.electronAPI.onDawSaveAction((action: DawSaveAction) => {
      navigate(
        action.kind === 'version' && action.projectId
          ? `/projects/${encodeURIComponent(action.projectId)}`
          : '/projects',
        {
          state: { dawSaveAction: action, receivedAt: Date.now() },
        }
      );
    });
  }, [navigate]);

  return null;
}

function AppContent() {
  const location = useLocation();

  // The Signal System redesign preview renders outside ProtectedRoute and
  // Layout — the screens are self-contained mockups with no queries behind
  // them, so neither a login nor the app's chrome should stand in front of it.
  if (isSignalUiEnabled() && location.pathname.startsWith('/signal')) {
    return <SignalPreviewRoutes />;
  }

  return (
    <AppThemeProvider>
      <NotificationProvider>
        <TransferErrorDialog />
        <ApolloProvider client={getClient()}>
          <AuthProvider>
            <ProtectedRoute>
              <ElectronProvider>
                <BridgeRequiredProvider>
                  <ProjectDirectoriesProvider>
                    <TransferSidebarProvider>
                      <TransferManagerProvider>
                        <DawSaveActionListener />
                        <Layout>
                          <AppRoutes />
                        </Layout>
                        {typeof window !== 'undefined' &&
                          window.electronAPI && <UpdateNotification />}{' '}
                      </TransferManagerProvider>
                    </TransferSidebarProvider>
                  </ProjectDirectoriesProvider>
                </BridgeRequiredProvider>
              </ElectronProvider>
            </ProtectedRoute>
          </AuthProvider>
        </ApolloProvider>
      </NotificationProvider>
    </AppThemeProvider>
  );
}

function App() {
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

  const appContent = (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );

  return (
    <HelmetProvider>
      {googleClientId ? (
        <GoogleOAuthProvider clientId={googleClientId}>
          {appContent}
        </GoogleOAuthProvider>
      ) : (
        appContent
      )}
    </HelmetProvider>
  );
}

export default App;
